import { UserOverview } from './user';

export enum AccountClassification {
    Asset = 'ASSET',
    Liability = 'LIABILITY',
    Equity = 'EQUITY',
    Revenue = 'REVENUE',
    Expense = 'EXPENSE',
}

export enum EntityTypeEnum {
    COMPANY = 'COMPANY',
    TRUST = 'TRUST',
    PARTNERSHIP = 'PARTNERSHIP',
    SOLE_TRADER = 'SOLE_TRADER',
    INDIVIDUAL = 'INDIVIDUAL',
    UNIT_TRUST = 'UNIT_TRUST',
    ESTATE = 'ESTATE',
    SELF_MANAGED_SUPERANNUATION_FUND = 'SELF_MANAGED_SUPERANNUATION_FUND',
    SUPERANNUATION_FUND = 'SUPERANNUATION_FUND',
    GOVERNMENT_ENTITY = 'GOVERNMENT_ENTITY',
    CLUB_OR_SOCIETY = 'CLUB_OR_SOCIETY',
    NOT_FOR_PROFIT = 'NOT_FOR_PROFIT',
    OTHER = 'OTHER',
    ACCOUNTING_PRACTICE = 'ACCOUNTING_PRACTICE',
    CHARITY = 'CHARITY',
    LOOK_THROUGH_COMPANY = 'LOOK_THROUGH_COMPANY',
    S_CORPORATION = 'S_CORPORATION',
}

export enum EntityProviderEnum {
    XERO = 'XERO',
    FYI = 'FYI',
}

export enum CountryCodeEnum {
    AU = 'AU',
    NZ = 'NZ',
    UK = 'UK',
}

export enum XeroReportTaxTypeEnum {
    OUTPUT = 'OUTPUT',
    INPUT = 'INPUT',
    EXEMPTOUTPUT = 'EXEMPTOUTPUT',
    EXEMPTINPUT = 'EXEMPTINPUT',
    EXEMPTCAPITAL = 'EXEMPTCAPITAL',
    EXEMPTEXPENSES = 'EXEMPTEXPENSES',
    EXEMPTEXPORT = 'EXEMPTEXPORT',
    ECOUTPUT = 'ECOUTPUT',
    ECOUTPUTSERVICES = 'ECOUTPUTSERVICES',
    ECINPUT = 'ECINPUT',
    ECACQUISITIONS = 'ECACQUISITIONS',
    CAPITALSALESOUTPUT = 'CAPITALSALESOUTPUT',
    CAPITALEXPENSESINPUT = 'CAPITALEXPENSESINPUT',
    MOSSSALES = 'MOSSSALES',
    REVERSECHARGES = 'REVERSECHARGES',
    INPUTTAXED = 'INPUTTAXED',
    BASEXCLUDED = 'BASEXCLUDED',
    GSTONCAPITALIMPORTS = 'GSTONCAPITALIMPORTS',
    GSTONIMPORTS = 'GSTONIMPORTS',
    NONE = 'NONE',
}

export type Entity = {
    id: string;
    firmId: string;
    name: string;
    legalName?: string;
    isArchived: boolean;
    archivedAt?: Date | null;
    archivedBy?: UserOverview | null;
    type: EntityTypeEnum;
    industry?: string;
    createdAt: Date;
    updatedAt: Date;
    financialYearEndMonth: number;
    financialYearEndDay: number;
    createdBy: UserOverview;
    groups: { name: string; id: string }[];
    activeWorkpapers: { name: string; id: string }[];
    avatarHexColour?: string;
    subscriptionActive: boolean;
    flaggedDeactivatedAt: Date | null;
    flaggedDeactivatedBy: string | null;
    // eslint-disable-next-line no-use-before-define
    charts: { id: string; provider: COAProvider | null }[];
    integrations: {
        xero?: {
            id: string;
            xeroUserId: string;
            xeroConnectionId: string;
            shortCode?: string;
            name: string;
            createdAt: Date;
            updatedAt: Date;
            refreshedAt: Date;
            createdBy?: UserOverview;
            taxRatesRefreshedAt?: Date | null;
            entityDetailsRefreshedAt?: Date | null;
            authorisedBy?: { email: string; firstName: string; lastName: string };
        };
        fyi?: { id: number; name: string; createdAt: Date; updatedAt: Date };
        atosa: { id: string; name: string; createdAt: Date; updatedAt: Date; createdBy?: UserOverview };
    };
    remainingWorkpapers?: number | null;
    registrationNumber?: string | null;
};

export type EntityTemplateCsv = {
    'Name *': string;
    'Legal Name *': string;
    'Financial Year End Day *': string;
    'Financial Year End Month *': string;
    'Entity Type *': string;
    Group: string;
};

export type EntityRowCsv = {
    name: string;
    legalName: string;
    financialYearEndDay: number;
    financialYearEndMonth: number;
    type: EntityTypeEnum;
    groups: string[];
};

export type XeroReportTaxType = {
    name?: string;
    type: XeroReportTaxTypeEnum;
    countryCode: CountryCodeEnum[];
    canCreate: boolean;
};

export type EntityTaxRate = {
    id: string;
    firmId: string;
    entityId: string;
    sequence: number;
    name: string;
    type: AccountClassification[];
    providerId?: string | null;
    provider?: EntityProviderEnum | null;
    xeroTaxType?: string | null;
    xeroReportTaxType?: XeroReportTaxType | null;
    rate: number;
    createdBy: UserOverview;
    createdAt: Date;
    updatedAt: Date;
    refreshedAt: Date;
    usedOnJournal?: boolean;
};

export type EntityRefreshedDates = {
    entityId: string;
    xeroTaxRatesRefreshedAt: Date;
    xeroEntityDetailsRefreshedAt: Date;
};

export enum COAProvider {
    Xero = 'XERO',
}

export type COA = {
    id: string;
    firmId: string;
    entityId: string;
    provider: COAProvider | null;
    createdAt: Date;
    updatedAt: Date;
    refreshedAt: Date | null;
    createdBy: UserOverview;
    refreshedBy: UserOverview | null;
    accounts: number;
};

export enum EntityAccountNature {
    debit = 'DEBIT',
    credit = 'CREDIT',
}
export type EntityAccountMapping = {
    id: string;
    code: string | null;
    name: string;
    accountId: string;
    chartId: string;
    chartProvider: COAProvider;
};

export type EntityAccount = {
    id: string;
    chartId: string;
    firmId: string;
    provider: COAProvider | null;
    providerId: string | null;
    code: string | null;
    name: string | null;
    description: string;
    type: string;
    class: AccountClassification;
    reportCode: string;
    nature: EntityAccountNature;
    canJournal: boolean;
    isTaxAccount: boolean;
    createdAt: Date;
    updatedAt: Date;
    createdBy: string | null;
    deletedAt: Date | null;
    mappings?: EntityAccountMapping[];
};
export type OpenWorkpapersReflectReport = {
    workpaperId: string;
    result: 'success' | 'error';
    accounts: {
        id: string;
        entityAccountId?: string | null;
        code?: string | null;
        name: string;
        result: 'success' | 'error';
        errorMessage?: string;
    }[];
};

export type COASyncReport = {
    createdCount: number;
    updatedCount: number;
    deletedCount: number;
    errors: string[];
    details: {
        created: Array<{ id: string; code: string | null; name: string }>;
        updated: Array<{ id: string; code: string | null; name: string }>;
        deleted: Array<{ id: string; code: string | null; name: string }>;
    };
    workpaperSyncResults: OpenWorkpapersReflectReport[];
};

export type ManualCOAInput = {
    code: string;
    name: string;
    description?: string;
    type: string;
    class: AccountClassification;
    reportCode: string;
}[];
